<script setup>
import { ListBulletIcon } from "@heroicons/vue/24/solid";
import { ExclamationTriangleIcon } from "@heroicons/vue/24/outline";
import { DateTime } from "luxon";
import { computed, ref, watchEffect } from "vue";
import { useI18n } from "vue-i18n";

import { SortButton, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import globals from "../globals";
import CopySpan from "./CopySpan.vue";

const { t } = useI18n();

const props = defineProps({
  options: {
    type: Array,
    required: true,
  },
  isCapable: {
    type: Function,
    required: true,
  },
  value: {
    type: String,
    required: false,
  },
  onSelect: {
    type: Function,
    required: true,
  },
  highlightedCapabilities: {
    type: Array,
    required: false,
  },
});

watchEffect(() => {
  const eligableAgents = props.options.filter(props.isCapable);
  if (eligableAgents.length === 1) props.onSelect(eligableAgents[0].ID);
});

const selectedAgent = computed(() => {
  return props.options.find((agent) => agent.ID === props.value);
});

const sortBy = ref("Name");
const sortDesc = ref(false);

function handleSortChange(column) {
  if (sortBy.value === column) {
    sortDesc.value = !sortDesc.value;
    return;
  }

  sortBy.value = column;
  sortDesc.value = false;
}

const sortedOptions = computed(() => {
  const sorted = [...props.options].sort(globals.smartSortFunc(globals.sortObjBy(sortBy.value)));
  const orderedSorted = sortDesc.value ? sorted.reverse() : sorted;

  // Partition items: capable first, then incapable
  const incapable = orderedSorted.filter((agent) => !props.isCapable(agent));
  const capable = orderedSorted.filter((agent) => props.isCapable(agent));

  return [...capable, ...incapable];
});

const capabilitySummaryMap = computed(() => {
  const map = {};

  props.options.forEach((agent) => {
    const caps = agent.Capabilities || {};
    const summaries = Object.entries(caps)
      .map(([key, value]) => {
        let displayName = t("fields.agents.capabilityNames." + key) || key;
        // Special handling for certain capabilities
        if (
          key === "os-release" ||
          (key === "ipset" && !value.command_available) ||
          (key === "pf" && !value.command_available)
        )
          return null;
        if (key === "windows_firewall" && value.available_rules) {
          displayName = `${displayName} (${value.available_rules.length} rules)`;
        }
        if (key === "ipset" && value.command_available) {
          const v4Rules = value.ipsets?.filter((ipset) => ipset.address_family === "inet").length || 0;
          const v6Rules = value.ipsets?.filter((ipset) => ipset.address_family === "inet6").length || 0;
          displayName = `${displayName} (${v4Rules} IPv4, ${v6Rules} IPv6)`;
        }
        if (key === "pf" && value.command_available) {
          const tableCount = value.tables?.length || 0;
          displayName = `${displayName} (${tableCount} tables)`;
        }

        return {
          key: key,
          displayName: displayName,
          isHighlighted: !props.highlightedCapabilities || props.highlightedCapabilities?.includes(key) || false,
        };
      })
      .filter((value) => value != null);

    // Sort: highlighted first, then non-highlighted
    map[agent.ID] = summaries.sort((a, b) => {
      if (a.isHighlighted && !b.isHighlighted) return -1;
      if (!a.isHighlighted && b.isHighlighted) return 1;
      return 0;
    });
  });

  return map;
});
</script>

<template>
  <div
    v-if="options.length === 0"
    class="flex flex-col items-center space-y-2 rounded-lg border bg-background p-6 shadow-sm transition-colors"
  >
    <ListBulletIcon class="h-16 w-16" />
    <span>{{ t("pages.wizard.steps.agent.empty.title") }}</span>
    <router-link to="/admin/agents" class="text-sm text-muted-foreground underline">
      {{ t("pages.wizard.steps.agent.empty.link") }}
    </router-link>
  </div>
  <fieldset v-else>
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>
            <span class="sr-only">{{ t("msg.select") }}</span>
          </TableHead>
          <TableHead :aria-sort="sortBy === 'Name' ? (sortDesc ? 'descending' : 'ascending') : undefined">
            <SortButton @click="handleSortChange('Name')">
              {{ t("pages.wizard.steps.agent.table.columns.Name") }}
            </SortButton>
          </TableHead>
          <TableHead>
            {{ t("pages.wizard.steps.agent.table.columns.Info") }}
          </TableHead>
          <TableHead>
            {{ t("pages.wizard.steps.agent.table.columns.Capabilities") }}
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        <TableRow
          v-for="(agent, i) in sortedOptions"
          :key="agent.ID"
          :data-testid="`agent-${agent.ID}`"
          :class="{
            'cursor-pointer': isCapable(agent),
            'cursor-not-allowed text-muted-foreground': !isCapable(agent),
          }"
          @click="isCapable(agent) ? onSelect(agent.ID) : undefined"
        >
          <TableCell
            :tooltip-text="!isCapable(agent) ? 'Agent is not capable of granting the Knoc type selected' : null"
          >
            <label class="relative flex cursor-pointer items-center">
              <input
                type="radio"
                name="agent-select"
                class="peer sr-only"
                :value="agent.ID"
                :checked="value === agent.ID"
                :disabled="!isCapable(agent)"
                :aria-label="
                  !isCapable(agent)
                    ? `Agent '${agent.Name}' is not capable of granting the Knoc type selected`
                    : `Select agent '${agent.Name}'`
                "
                :aria-disabled="!isCapable(agent)"
              />

              <span
                aria-hidden="true"
                :class="{
                  'formkit-decorator relative block aspect-[1/1] w-[1em] select-none rounded-full border border-neutral-400 bg-white text-lg text-transparent shadow ring-blue-500 group-[]/multistep:shadow-none group-[]/repeater:shadow-none peer-focus-visible:ring-2 peer-focus-visible:ring-offset-1 dark:border-neutral-500 dark:bg-transparent dark:ring-offset-blue-500 dark:peer-focus-visible:ring-1': true,
                  '!bg-blue-50 !text-blue-600 dark:!border-blue-600 dark:!bg-blue-900 dark:!text-blue-400':
                    value === agent.ID,
                  '!cursor-not-allowed bg-neutral-100 grayscale dark:bg-neutral-600/50': !isCapable(agent),
                }"
              />
            </label>
          </TableCell>
          <TableCell
            :tooltip-text="!isCapable(agent) ? 'Agent is not capable of granting the Knoc type selected' : null"
          >
            {{ agent.Name }}
          </TableCell>
          <TableCell
            :tooltip-text="!isCapable(agent) ? 'Agent is not capable of granting the Knoc type selected' : null"
          >
            <div v-if="agent.LastSeen" class="flex flex-col space-y-1">
              <span v-if="agent.Capabilities?.['os-release']">
                {{ agent.Capabilities["os-release"].NAME }}
                {{ agent.Capabilities["os-release"].VERSION }}
              </span>
              <span v-else>Unknown</span>
              <span class="text-sm text-muted-foreground">
                Last seen {{ DateTime.fromISO(agent.LastSeen).toRelative() }}
              </span>
            </div>
            <span v-else>Awaiting Connection</span>
          </TableCell>
          <TableCell
            :tooltip-text="!isCapable(agent) ? 'Agent is not capable of granting the Knoc type selected' : null"
          >
            <div v-if="agent.LastSeen" class="flex flex-col space-y-1">
              <div v-if="capabilitySummaryMap[agent.ID]?.length > 0">
                <strong>Capabilities: </strong>
                <template v-for="(cap, index) in capabilitySummaryMap[agent.ID]" :key="cap.key">
                  <span :class="{ 'text-muted-foreground': !cap.isHighlighted }">
                    {{ cap.displayName }}
                  </span>
                  <span v-if="index < capabilitySummaryMap[agent.ID].length - 1">, </span>
                </template>
              </div>
            </div>
            <CopySpan v-else :value="agent.Token" />
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  </fieldset>
  <div
    v-if="Boolean(isCapable) && Boolean(selectedAgent) && !isCapable(selectedAgent)"
    class="border-warning/50 text-warning dark:border-warning [&>svg]:text-warning relative col-span-2 w-full rounded-lg border p-4 [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg~*]:pl-7"
    role="alert"
  >
    <ExclamationTriangleIcon class="h-4 w-4" />
    <p class="text-sm [&_p]:leading-relaxed">
      The Knoc type selected is not compatible with the agent's version. You will need to update the agent using your
      operating system's package manager to use this Knoc.
    </p>
  </div>
</template>
